
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import HeroBanner from '../components/HeroBanner';
import AboutSection from '../components/AboutSection';
import SkillsSection from '../components/SkillsSection';
import ExperienceSection from '../components/ExperienceSection';
import EducationSection from '../components/EducationSection';
import PersonalSection from '../components/PersonalSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import { toast } from "sonner";

const Index = () => {
  useEffect(() => {
    // Update document title
    document.title = "Payton Hunter | Psychology Graduate | Client Liaison | AI-Curious Strategist";
    
    // Log page visit to console
    console.log('Page visit: home');
    
    // Show welcome toast when page loads
    toast("Welcome to my portfolio!", {
      description: "Thank you for visiting! Scroll down to learn more about my experience and skills.",
      duration: 5000,
    });
    
    // Smooth scrolling for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const href = this.getAttribute('href');
        if (href) {
          document.querySelector(href)?.scrollIntoView({
            behavior: 'smooth'
          });
        }
      });
    });
    
    // Scroll animations with intersection observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          // Use HTMLElement type assertion to ensure style property is accessible
          const target = entry.target as HTMLElement;
          target.style.opacity = '1';
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('section > div').forEach(el => {
      el.classList.add('opacity-0');
      observer.observe(el);
    });
    
    // Add transition effects for interactive elements
    document.querySelectorAll('button, a, .skill-badge, .skill-badge-alt').forEach(el => {
      el.classList.add('transition-all', 'duration-300');
    });
    
    // Add custom styles for company logos
    const style = document.createElement('style');
    style.innerHTML = `
      .company-logo {
        transition: all 0.4s ease;
        filter: drop-shadow(0 0 0 transparent);
      }
      .group:hover .company-logo {
        transform: scale(1.2);
        filter: drop-shadow(0 5px 15px rgba(0,0,0,0.1));
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      observer.disconnect();
      document.head.removeChild(style);
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>Payton Hunter | Psychology Graduate | Client Liaison | AI-Curious Strategist</title>
        <meta name="description" content="Explore Payton Hunter's resume—Psychology graduate with forensic anthropology focus, skilled in client experience, communication, and AI practices." />
        <meta name="keywords" content="Payton Hunter, psychology graduate, AI literacy, adaptability, communication skills, client relations, systems improvement" />
        <meta property="og:title" content="Payton Hunter | Psychology Graduate | Client Liaison | AI-Curious Strategist" />
        <meta property="og:description" content="Explore Payton Hunter's resume—Psychology graduate with forensic anthropology focus, skilled in client experience, communication, and AI practices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://peytonhuntersresume.vercel.app" />
        <meta property="og:image" content="https://peytonhuntersresume.vercel.app/lovable-uploads/ea557aa4-857c-4d59-91b6-441b5daab0c5.png" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://peytonhuntersresume.vercel.app" />
        
        {/* Additional schema.org structured data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "url": "https://peytonhuntersresume.vercel.app",
              "name": "Payton Hunter's Professional Portfolio",
              "description": "Explore Payton Hunter's resume—Psychology graduate with forensic anthropology focus, skilled in client experience, communication, and AI practices."
            }
          `}
        </script>
      </Helmet>
      
      <Navbar />
      <HeroBanner />
      <AboutSection />
      <SkillsSection />
      <ExperienceSection />
      <EducationSection />
      <PersonalSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default Index;
